import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TicketRequest } from 'src/app/models/ticketOrder';
import { BenefitsService } from '../../../services/benefits.service';
import { AuthService } from 'src/app/services/auth.service';
import { TicketService } from 'src/app/services/ticket.service';
import { Subscription } from 'rxjs';
import { ModalField } from 'src/app/modal/modal.component';

// Declare Flowbite types
declare var initFlowbite: () => void;
declare interface ModalInterface {
  show(): void;
  hide(): void;
}
declare class Modal {
  constructor(element: HTMLElement | null, options?: any);
  show(): void;
  hide(): void;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  providers: [MessageService],
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  commandeModalId = 'nouvelle-commande-modal';
  componyWalletAddress: string = '';
  tickets: any[] = [];
  ticketAmount: number = 0;
  amountLabel: string = 'Montant';
  calculatedAmount: number | null = null;
  isTicketRestaurantSelected: boolean = false;

  newOrderFields: ModalField[] = []; // Initialize as empty

  private ticketChangesSubscription: Subscription | undefined;
  private amountChangesSubscription: Subscription | undefined;

  constructor(
    private messageService: MessageService,
    private benefitsService: BenefitsService,
    private authService: AuthService,
    private ticketService: TicketService,
  ) {}

  ngOnInit(): void {
    this.authService.getUser().subscribe((result) => {
      this.componyWalletAddress = result?.account ?? '';
      this.ticketOrderForm.get('account')?.setValue(this.componyWalletAddress);
    });

   this.ticketService.getAllTickets().subscribe((re) => {
  this.tickets = Array.isArray(re.tickets)
    ? re.tickets.filter((t: any) => t.type === 'B2B')
    : [];

      // Define newOrderFields *after* tickets are fetched
      this.newOrderFields = [
        {
          controlName: 'ticket',
          label: 'Type de recharge *',
          type: 'select',
          placeholder: 'Sélectionner le type',
          required: true,
          options: this.tickets.map((ticket) => ({
            id: ticket.id,
            label: ticket.label,
          })),
        },
        {
          controlName: 'amount',
          label: this.amountLabel + ' *', // Use current amountLabel
          type: 'number',
          placeholder: this.isTicketRestaurantSelected
            ? 'Entrer le nombre de tickets'
            : 'Entrer le montant', // Use current placeholder logic
          required: true,
        },
      ];
    });

    this.ticketService.getTicketAmount().subscribe(
      (res) => {
        this.ticketAmount = res.data.ticketAmount;
      },
      (error) => {
        console.error('Failed to fetch ticket amount:', error);
      },
    );

    this.ticketChangesSubscription = this.ticketOrderForm
      .get('ticket')
      ?.valueChanges.subscribe((selectedTicketId) => {
        this.handleTicketChange(selectedTicketId);
      });
  }

  ngAfterViewInit(): void {
    // Ensure Flowbite is initialized after view is ready
    if (typeof initFlowbite === 'function') {
      initFlowbite();
    } else {
      console.error('initFlowbite function not found.');
    }
  }

  ngOnDestroy(): void {
    this.ticketChangesSubscription?.unsubscribe();
    this.amountChangesSubscription?.unsubscribe();
  }

  handleTicketChange(selectedTicketId: any): void {
    //console.log('function called');
    this.amountChangesSubscription?.unsubscribe();
    this.calculatedAmount = null;

    const selectedTicket = this.tickets.find((t) => t.id == selectedTicketId);

    //console.log('selectedTicket', selectedTicket);

    if (
      selectedTicket &&
      selectedTicket.label?.toLowerCase() === 'tickets restaurant'
    ) {
      this.isTicketRestaurantSelected = true;
      const label = 'Nombre de tickets';
      const placeholder = 'Entrer le nombre de tickets';

      this.amountLabel = label;
      // Update the label and placeholder in the dynamic fields definition
      const amountField = this.newOrderFields.find(
        (f) => f.controlName === 'amount',
      );
      if (amountField) {
        amountField.label = this.amountLabel + ' *';
        amountField.placeholder = placeholder;
      }

      this.ticketOrderForm
        .get('amount')
        ?.setValidators([
          Validators.required,
          Validators.pattern('^[0-9]*$'),
          Validators.min(1),
        ]);
      this.ticketOrderForm.get('amount')?.updateValueAndValidity();

      this.calculateTotalAmount(this.ticketOrderForm.get('amount')?.value);

      this.amountChangesSubscription = this.ticketOrderForm
        .get('amount')
        ?.valueChanges.subscribe((value) => {
          this.calculateTotalAmount(value);
        });
    } else {
      this.isTicketRestaurantSelected = false;
      this.amountLabel = 'Montant';

      const amountField = this.newOrderFields.find(
        (f) => f.controlName === 'amount',
      );
      if (amountField) {
        amountField.label = this.amountLabel + ' *';
        amountField.placeholder = 'Entrer le montant';
      }

      this.ticketOrderForm
        .get('amount')
        ?.setValidators([Validators.required, Validators.min(1)]);
      this.ticketOrderForm.get('amount')?.updateValueAndValidity();

      this.calculatedAmount = null;
    }
  }

    /** Sélection d'un type de recharge (cartes du modal) */
  selectTicket(id: any): void {
    const control = this.ticketOrderForm.get('ticket');
    // Même format qu'avant (le <select> envoyait l'id sous forme de texte)
    control?.setValue(String(id) as any);
    control?.markAsTouched();
  }

  /** 100000 -> "100 000 XOF" */
  formatXof(value: any): string {
    const n = Number(value) || 0;
    return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }).replace(/\u202f|\u00a0/g, ' ') + ' XOF';
  }

  calculateTotalAmount(numberOfTickets: any): void {
    const num = Number(numberOfTickets);
    if (!isNaN(num) && num > 0 && this.ticketAmount > 0) {
      this.calculatedAmount = num * this.ticketAmount;
    } else {
      this.calculatedAmount = null;
    }
  }

  ticketOrderForm = new FormGroup({
    account: new FormControl(this.componyWalletAddress, [Validators.required]),
    ticket: new FormControl(null, [Validators.required]),
    amount: new FormControl(null, [Validators.required, Validators.min(1)]),
  });

  onSubmit() {
   // console.log('ticketOrderForm', this.ticketOrderForm.value);
    if (this.ticketOrderForm.valid) {
      const account = this.componyWalletAddress;

      let value;
      if (this.isTicketRestaurantSelected) {
        value = this.calculatedAmount;
      } else {
        value = this.ticketOrderForm.get('amount')?.value ?? null;
      }
      const id = this.ticketOrderForm.get('ticket')?.value ?? null;

      const ticketOrder = new TicketRequest(account, id, value);

      this.benefitsService.createTicket(ticketOrder).subscribe({
        next: (response) => {
          this.show();
          this.ticketOrderForm.reset({ account: this.componyWalletAddress });
          this.ticketOrderForm.markAsUntouched();
          this.amountLabel = 'Montant';
          this.isTicketRestaurantSelected = false;
          this.calculatedAmount = null;
          this.closeModal();
        },
        error: (error) => {
          console.error('Failed to create ticket:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              'Une erreur est survenue lors de votre commande, veuillez réessayer.',
          });
        },
      });
    } else {
      Object.keys(this.ticketOrderForm.controls).forEach((key) => {
        const control = this.ticketOrderForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  show() {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Commande envoyée !',
    });
  }

  private getModalInstance(): ModalInterface | null {
    const modalElement = document.getElementById(this.commandeModalId);
    if (modalElement) {
      return new Modal(modalElement);
    }
    console.warn(`Modal element with ID '${this.commandeModalId}' not found.`);
    return null;
  }

  closeModal(): void {
     this.ticketOrderForm.markAsUntouched();
    const modal = this.getModalInstance();
    if (modal) {
      modal.hide();
      const backdrop = document.querySelector('[modal-backdrop]');
      backdrop?.remove();
      document.body.classList.remove('overflow-hidden');
    }
  }

  openModal(): void {
    const modal = this.getModalInstance();
    if (modal) {
      modal.show();
    }
  }
}
